import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  MenuItem,
  InputAdornment,
  alpha,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Save as SaveIcon,
  Build as MaintenanceIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Warning as PriorityIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import { formatFieldErrorsMessage, rejectConfirmValidation } from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import { canManageMaintenanceWorkflow, canModifyMaintenanceRequest } from '../../../utils/maintenancePermissions';
import { MAINTENANCE_STATUS_COLUMNS } from '../../../utils/maintenanceStatus';
import StatusColumnPicker from '../../common/StatusColumnPicker';
import SearchableSelect from '../../common/SearchableSelect';
import { inputRootSx, makePickerSlots } from '../../common/formFieldSx';
import TextField from '@mui/material/TextField';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 800: '#1E40AF', 900: '#1E3A8A' };
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const FORM_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse   { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  @keyframes neon-ping    { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }

  .MuiOutlinedInput-input,
  .MuiInputBase-input {
    color: #E2E8F0 !important;
    -webkit-text-fill-color: #E2E8F0 !important;
    caret-color: #38BDF8 !important;
  }
  .MuiOutlinedInput-input:-webkit-autofill,
  .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 100px #0f1f38 inset !important;
    -webkit-text-fill-color: #E2E8F0 !important;
  }
`;

const PICKER_CSS = `
  .MuiPickersPopper-root .MuiPaper-root,
  .MuiDateCalendar-root,
  .MuiPickersLayout-root {
    background: #0a1628 !important;
    color: #E2E8F0 !important;
    border: 1px solid rgba(56,189,248,0.22) !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 32px rgba(0,0,0,0.65) !important;
  }
  .MuiDayCalendar-weekDayLabel {
    color: rgba(56,189,248,0.65) !important;
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 0.65rem !important;
    font-weight: 600 !important;
    letter-spacing: 1px !important;
  }
  .MuiPickersDay-root {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.85rem !important;
    background: transparent !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersDay-root:hover {
    background: rgba(56,189,248,0.18) !important;
    color: #38BDF8 !important;
    box-shadow: 0 0 8px rgba(56,189,248,0.3) !important;
  }
  .MuiPickersDay-root.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    border: 2px solid #38BDF8 !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.6) !important;
  }
  .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid rgba(56,189,248,0.65) !important;
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.08) !important;
  }
  .MuiPickersDay-root.Mui-disabled,
  .MuiPickersDay-root[aria-disabled="true"] {
    color: rgba(100, 116, 139, 0.45) !important;
    -webkit-text-fill-color: rgba(100, 116, 139, 0.45) !important;
    background: rgba(15, 23, 42, 0.45) !important;
    opacity: 1 !important;
    text-decoration: none !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
  }
  .MuiPickersDay-root.Mui-disabled:hover,
  .MuiPickersDay-root[aria-disabled="true"]:hover {
    color: rgba(100, 116, 139, 0.45) !important;
    -webkit-text-fill-color: rgba(100, 116, 139, 0.45) !important;
    background: rgba(15, 23, 42, 0.45) !important;
    box-shadow: none !important;
  }
  .MuiPickersDay-root.Mui-disabled.MuiPickersDay-today,
  .MuiPickersDay-root[aria-disabled="true"].MuiPickersDay-today {
    border-color: rgba(71, 85, 105, 0.5) !important;
    color: rgba(100, 116, 139, 0.5) !important;
    background: rgba(15, 23, 42, 0.5) !important;
  }
  .MuiPickersCalendarHeader-label {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
    font-size: 1rem !important;
  }
  .MuiPickersArrowSwitcher-button,
  .MuiPickersCalendarHeader-switchViewButton {
    color: rgba(56,189,248,0.75) !important;
  }
  .MuiPickersArrowSwitcher-button:hover,
  .MuiPickersCalendarHeader-switchViewButton:hover {
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersLayout-toolbar,
  .MuiDatePickerToolbar-root {
    background: rgba(5, 13, 26, 0.96) !important;
    border-bottom: 1px solid rgba(56,189,248,0.15) !important;
  }
  .MuiPickersToolbarText-root,
  .MuiDatePickerToolbar-title {
    color: #E2E8F0 !important;
    font-family: 'Rajdhani', sans-serif !important;
    font-weight: 700 !important;
  }
  .MuiPickersActionBar-root {
    border-top: 1px solid rgba(56,189,248,0.12) !important;
    background: rgba(5, 13, 26, 0.9) !important;
  }
  .MuiPickersActionBar-root .MuiButton-root {
    color: rgba(56,189,248,0.9) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 600 !important;
  }
  .MuiYearCalendar-root button,
  .MuiMonthCalendar-root button {
    color: #CBD5E1 !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .MuiYearCalendar-root button:hover,
  .MuiMonthCalendar-root button:hover {
    background: rgba(56,189,248,0.1) !important;
    color: #38BDF8 !important;
  }
  .MuiYearCalendar-root button.Mui-selected,
  .MuiMonthCalendar-root button.Mui-selected {
    background: linear-gradient(135deg,#38BDF8 60%,#0EA5E9 100%) !important;
    color: #050d1a !important;
    font-weight: 700 !important;
    box-shadow: 0 0 12px rgba(56,189,248,0.5) !important;
    border-radius: 8px !important;
  }
  .MuiMonthCalendar-root .Mui-disabled,
  .MuiYearCalendar-root .Mui-disabled {
    color: rgba(100, 116, 139, 0.45) !important;
    opacity: 1 !important;
    background: transparent !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root,
  .MuiPickersOutlinedInput-root {
    border-radius: 10px !important;
    background: rgba(15, 31, 56, 0.7) !important;
    backdrop-filter: blur(8px) !important;
    transition: box-shadow 0.2s ease, border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.18) !important;
    transition: border-color 0.2s ease !important;
  }
  .MuiPickersTextField-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersTextField-root .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.4) !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
  .MuiPickersOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: rgba(56,189,248,0.65) !important;
    border-width: 1.5px !important;
  }
  .MuiPickersTextField-root .MuiOutlinedInput-root.Mui-focused,
  .MuiPickersOutlinedInput-root.Mui-focused {
    box-shadow: 0 0 0 3px rgba(56,189,248,0.12), 0 0 14px rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.875rem !important;
    color: rgba(56,189,248,0.45) !important;
  }
  .MuiPickersTextField-root .MuiInputLabel-root.Mui-focused {
    color: rgba(56,189,248,0.85) !important;
    font-weight: 600 !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionStart .MuiSvgIcon-root {
    color: rgba(56,189,248,0.45) !important;
    font-size: 18px !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root {
    color: rgba(56,189,248,0.35) !important;
    padding: 6px !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiIconButton-root:hover {
    color: #38BDF8 !important;
    background: rgba(56,189,248,0.1) !important;
  }
  .MuiPickersTextField-root .MuiInputAdornment-positionEnd .MuiSvgIcon-root {
    font-size: 17px !important;
  }
  .MuiPickersSectionList-root {
    color: rgba(226,232,240,0.85) !important;
    font-family: 'DM Sans', sans-serif !important;
    font-size: 0.9rem !important;
  }
  .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-separator {
    color: rgba(226,232,240,0.85) !important;
    -webkit-text-fill-color: rgba(226,232,240,0.85) !important;
  }
  .MuiPickersSectionList-sectionContent[data-value=""] {
    color: rgba(56,189,248,0.3) !important;
    -webkit-text-fill-color: rgba(56,189,248,0.3) !important;
  }
  .MuiPickersSectionList-section:focus .MuiPickersSectionList-sectionContent,
  .MuiPickersSectionList-sectionContent:focus {
    background: rgba(56,189,248,0.15) !important;
    border-radius: 3px !important;
    color: #38BDF8 !important;
    -webkit-text-fill-color: #38BDF8 !important;
    outline: none !important;
  }
`;

const TEXT_BODY = '#E2E8F0';

const isCompletedStatus = (s) => String(s ?? '').toLowerCase() === 'completed';
const isFutureDay       = (d) => dayjs(d).startOf('day').isAfter(dayjs().startOf('day'));

const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

const StyledField = ({ sx, ...props }) => (
  <TextField {...props} sx={{ ...inputRootSx, ...sx }} />
);

const StyledSelect = ({ label, value, onChange, children, required, startIcon, helperText, error, disabled, sx }) => (
  <FormControl fullWidth required={required} error={error} disabled={disabled} sx={{ flex: 1, ...inputRootSx, ...sx }}>
    <InputLabel sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45), '&.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 } }}>
      {label}
    </InputLabel>
    <Select
      value={value} onChange={onChange} label={label}
      startAdornment={startIcon ? <InputAdornment position="start">{startIcon}</InputAdornment> : undefined}
      MenuProps={{
        PaperProps: {
          sx: {
            background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`,
            border: `1px solid ${alpha(NEON, 0.2)}`,
            boxShadow: `0 8px 32px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`,
            backdropFilter: 'blur(12px)',
            '& .MuiMenuItem-root': {
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', color: alpha(TEXT_BODY, 0.85), transition: 'all 0.15s ease',
              '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.12)}, ${alpha(B[700], 0.08)})`, color: NEON },
              '&.Mui-selected': { background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.12)})`, color: NEON, fontWeight: 600, '&:hover': { background: `linear-gradient(135deg, ${alpha(NEON, 0.22)}, ${alpha(B[700], 0.15)})` } },
            },
          },
        },
      }}
      sx={{
        borderRadius: '10px', fontFamily: "'DM Sans', sans-serif",
        color: alpha(TEXT_BODY, 0.85), background: alpha(SURFACE_UP, 0.7), backdropFilter: 'blur(8px)',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.18) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}, 0 0 14px ${alpha(NEON, 0.1)}` },
        '& .MuiSelect-select': { color: `${alpha(TEXT_BODY, 0.85)} !important`, WebkitTextFillColor: `${alpha(TEXT_BODY, 0.85)} !important` },
        '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      }}
    >
      {children}
    </Select>
    {helperText && (
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', letterSpacing: 0.3, color: error ? '#F87171' : alpha(TEXT_BODY, 0.9), mt: 0.4, mx: 1.75 }}>
        {helperText}
      </Typography>
    )}
  </FormControl>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const MaintenanceForm = () => {
  const navigate              = useNavigate();
  const { id }                = useParams();
  const { user: currentUser } = useAuth();
  const isEditMode            = Boolean(id);

  const [formData, setFormData] = useState({
    title: '', description: '', location: '',
    note: '',
    reported_date:  dayjs().format('YYYY-MM-DD'),
    type:           'repair',
    priority:       'medium',
    status:         'pending',
    user_ids:       [],
    completion_date: '',
  });

  const [users,                   setUsers]                   = useState([]);
  const [dataLoading,             setDataLoading]             = useState(true);
  const [loading,                 setLoading]                 = useState(false);
  const [error,                   setError]                   = useState(null);
  const [successMessage,          setSuccessMessage]          = useState('');
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [completionPickerOpen, setCompletionPickerOpen] = useState(false);

  const canManageWorkflow   = canManageMaintenanceWorkflow(currentUser);
  const assigneeOptions = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.name })),
    [users]
  );
  const maintenanceTypes = [
    { value: 'repair', label: 'Repair' },
    { value: 'replacement', label: 'Replacement' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'other', label: 'Other' },
  ];
  const priorityLevels = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'maintenance',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  // ── Inject CSS ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const cssId = 'maintenanceform-ds-css';
    let el = document.getElementById(cssId);
    if (!el) { el = document.createElement('style'); el.id = cssId; document.head.appendChild(el); }
    el.innerHTML = FORM_CSS + PICKER_CSS;
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    const fetchData = async () => {
      try {
        setDataLoading(true);
        const [usersRes] = await Promise.all([api.get('/users?active_only=1')]);
        setUsers(usersRes.data);
        if (isEditMode) {
          const res  = await api.get(`/maintenances/${id}`);
          const data = res.data;
          if (!canModifyMaintenanceRequest(currentUser, data)) {
            setError('You can only edit if you reported this request, or you are a Team Leader / Maintenance bureau member.');
            setDataLoading(false);
            return;
          }
          setFormData({
            title:           data.title           || '',
            description:     data.description     || '',
            location:        data.location        || '',
            note:            data.note            || '',
            reported_date:   data.reported_date ? data.reported_date.split('T')[0] : dayjs().format('YYYY-MM-DD'),
            type:            data.type            || 'repair',
            priority:        data.priority        || 'medium',
            status:          String(data.status || 'pending').toLowerCase(),
            user_ids:        (data.assignees || []).map((member) => String(member.id)),
            completion_date: data.completion_date ? data.completion_date.split('T')[0] : '',
          });
        }
      } catch { setError('Failed to load data. Please try again.'); }
      finally { setDataLoading(false); }
    };
    fetchData();
  }, [currentUser, id, isEditMode]);

  useEffect(() => {
    if (!isCompletedStatus(formData.status)) setCompletionPickerOpen(false);
  }, [formData.status]);

  const collectRequiredFieldErrors = () => {
    const errs = {};
    if (!formData.title?.trim())       errs.title       = 'Title is required';
    if (!formData.description?.trim()) errs.description = 'Description is required';
    if (!formData.location?.trim())    errs.location    = 'Location is required';
    if (!formData.type)                errs.type        = 'Type is required';
    if (!formData.priority)            errs.priority    = 'Priority is required';
    return errs;
  };

  const validateForSubmit = () => {
    const errs = collectRequiredFieldErrors();
    if (canManageWorkflow) {
      if (!formData.user_ids.length) errs.user_ids = 'Select at least one assignee';
      if (isCompletedStatus(formData.status)) {
        if (!formData.completion_date) {
          errs.completion_date = 'Please set the completion date when status is Completed.';
        } else if (dayjs(formData.completion_date).isAfter(dayjs(), 'day')) {
          errs.completion_date = 'Completion date must be today or earlier.';
        }
      }
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const data = { ...formData, reported_date: formData.reported_date || dayjs().format('YYYY-MM-DD'), completion_date: formData.completion_date || null };
      if (isEditMode) {
        setValidationErrors({});
        setShowFieldErrors(false);
        setConfirmUpdateDialogOpen(true);
        return;
      }
      const fieldErrors = validateForSubmit();
      setValidationErrors(fieldErrors);
      if (Object.keys(fieldErrors).length > 0) {
        setShowFieldErrors(true);
        setError(formatFieldErrorsMessage(fieldErrors));
        return;
      }
      setShowFieldErrors(false);
      setLoading(true);
      await api.post('/maintenances', data);
      if (!isEditMode) formDraft.clearDraft();
      setSuccessMessage('Maintenance request created successfully!');
      try { localStorage.setItem('maintenanceListView', 'table'); } catch { /* ignore */ }
      setTimeout(() => navigate('/maintenances', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) {
      if (err.response?.data?.errors) setError(Object.values(err.response.data.errors).flat().join('\n'));
      else setError(err.response?.data?.message || 'Failed to save maintenance request. Please try again.');
    } finally { setLoading(false); }
  };

  const confirmUpdate = async () => {
    const fieldErrors = validateForSubmit();
    if (Object.keys(fieldErrors).length > 0) {
      rejectConfirmValidation({
        errors: fieldErrors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setShowFieldErrors,
      });
      return;
    }
    const payload = { ...formData, completion_date: formData.completion_date || null };
    try {
      setLoading(true);
      await api.put(`/maintenances/${id}`, payload);
      setSuccessMessage('Maintenance request updated successfully!');
      try { localStorage.setItem('maintenanceListView', 'table'); } catch { /* ignore */ }
      setTimeout(() => navigate('/maintenances', { state: { refreshList: Date.now() } }), 1500);
    } catch (err) { setError(err.response?.data?.message || 'Failed to update maintenance request'); }
    finally { setLoading(false); setConfirmUpdateDialogOpen(false); }
  };

  if (dataLoading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading maintenance form" size={56} variant="primary" />
    </Box>
  );

  if (!currentUser) return null;

  const submitValidationErrors = validateForSubmit();
  const canSubmit = Object.keys(submitValidationErrors).length === 0;
  const isCompleted = isCompletedStatus(formData.status);
  const completionPickerDisabled = !isCompleted;
  const completionPickerSlots = makePickerSlots('normal', <CalendarTodayIcon />)(completionPickerDisabled);
  const missingRequiredFields = isEditMode ? null : [
    !formData.title?.trim() && 'Title',
    !formData.description?.trim() && 'Description',
    !formData.location?.trim() && 'Location',
    !formData.type && 'Type',
    !formData.priority && 'Priority',
    canManageWorkflow && !formData.user_ids.length && 'Assign to',
    canManageWorkflow && isCompleted && !formData.completion_date && 'Completed On',
  ].filter(Boolean);

  const fieldHelper = (name) => (
    showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined
  );
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

        <Header
          title={isEditMode ? 'Edit Maintenance Request' : 'Create New Maintenance Request'}
          subtitle={isEditMode ? 'Update maintenance request information and details' : 'Create a new maintenance request for facility management'}
          icon={MaintenanceIcon}
          showBackButton
          onBackClick={() => navigate(-1)}
          backButtonText="Back to Maintenance"
        />

        <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage('')} />
        <FeedbackMessage message={error} severity="error" onClose={() => setError(null)} />

        {formDraft.showNotice && (
          <FormDraftNotice
            savedAt={formDraft.restoredAt}
            onDiscard={formDraft.discardDraft}
            onDismiss={formDraft.dismissNotice}
          />
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ── Request details ─────────────────────────────────────────── */}
            <SectionCard title="What needs fixing?" icon={<MaintenanceIcon />} delay={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <StyledField
                    fullWidth required label="Short title" name="title" value={formData.title}
                    onChange={handleChange} error={fieldHasError('title')} helperText={fieldHelper('title')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CategoryIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                  <StyledField
                    fullWidth required label="Location" name="location" value={formData.location}
                    onChange={handleChange} error={fieldHasError('location')} helperText={fieldHelper('location')}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />
                </Box>
                <StyledField
                  fullWidth required multiline rows={3} label="Description" name="description" value={formData.description}
                  onChange={handleChange} error={fieldHasError('description')} helperText={fieldHelper('description')}
                  InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>
                  <SearchableSelect
                    required
                    label="Request type"
                    value={formData.type}
                    onChange={(v) => {
                      setFormData((prev) => ({ ...prev, type: v }));
                      if (error) setError(null);
                      if (showFieldErrors && validationErrors.type) {
                        setValidationErrors((prev) => ({ ...prev, type: '' }));
                      }
                    }}
                    options={maintenanceTypes}
                    startIcon={<CategoryIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                  <SearchableSelect
                    required
                    label="Priority"
                    value={formData.priority}
                    onChange={(v) => {
                      setFormData((prev) => ({ ...prev, priority: v }));
                      if (error) setError(null);
                      if (showFieldErrors && validationErrors.priority) {
                        setValidationErrors((prev) => ({ ...prev, priority: '' }));
                      }
                    }}
                    options={priorityLevels}
                    startIcon={<PriorityIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                    sx={{ flex: 1 }}
                  />
                </Box>
                {(fieldHelper('type') || fieldHelper('priority')) && (
                  <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: '#F87171', mt: -1 }}>
                    {fieldHelper('type') || fieldHelper('priority')}
                  </Typography>
                )}
              </Box>
            </SectionCard>

            {/* ── Assignment & Timeline ───────────────────────────────────── */}
            {canManageWorkflow && (
              <SectionCard title="Workflow" icon={<AssignmentIcon />} delay={0.12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                  <StatusColumnPicker
                    columns={MAINTENANCE_STATUS_COLUMNS}
                    value={formData.status}
                    onChange={(v) => {
                      setFormData((prev) => ({
                        ...prev,
                        status: v,
                        ...(isCompletedStatus(v)
                          ? { completion_date: prev.completion_date || dayjs().format('YYYY-MM-DD') }
                          : { completion_date: '' }),
                      }));
                    }}
                    label="Board status"
                  />

                  <Box sx={{ width: '100%' }}>
                    <SearchableSelect
                      required
                      multiple
                      label="Assign to"
                      value={formData.user_ids}
                      onChange={(v) => {
                        setFormData((prev) => ({ ...prev, user_ids: v }));
                        if (error) setError(null);
                        if (showFieldErrors && validationErrors.user_ids) {
                          setValidationErrors((prev) => ({ ...prev, user_ids: '' }));
                        }
                      }}
                      options={assigneeOptions}
                      placeholder=""
                      startIcon={<PersonIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                      sx={{
                        width: '100%',
                        ...(fieldHasError('user_ids')
                          ? { '& .MuiFormHelperText-root': { color: '#F87171' } }
                          : {}),
                      }}
                    />
                    {fieldHelper('user_ids') && (
                      <Typography
                        sx={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '0.68rem',
                          color: '#F87171',
                          mt: 0.5,
                          ml: 1.75,
                        }}
                      >
                        {fieldHelper('user_ids')}
                      </Typography>
                    )}
                  </Box>

                  <StyledField
                    fullWidth multiline rows={2} label="Internal note" name="note" value={formData.note}
                    onChange={handleChange}
                    InputProps={{ startAdornment: <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}><DescriptionIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} /></InputAdornment> }}
                  />

                  {/* ── Completion date ────────────────────────────────────── */}
                  <DatePicker
                    key={`completion-${formData.status}`}
                    label="Completed On"
                    value={formData.completion_date ? dayjs(formData.completion_date) : null}
                    maxDate={dayjs()}
                    shouldDisableDate={isFutureDay}
                    open={completionPickerOpen}
                    onOpen={() => isCompleted && setCompletionPickerOpen(true)}
                    onClose={() => setCompletionPickerOpen(false)}
                    onChange={(v) => {
                      setFormData((prev) => ({ ...prev, completion_date: v?.isValid() ? v.format('YYYY-MM-DD') : '' }));
                      if (error) setError(null);
                      if (showFieldErrors && validationErrors.completion_date) {
                        setValidationErrors((prev) => ({ ...prev, completion_date: '' }));
                      }
                    }}
                    disabled={completionPickerDisabled}
                    slotProps={(() => {
                      const openPicker = () => {
                        if (!completionPickerDisabled) setCompletionPickerOpen(true);
                      };
                      return {
                        ...completionPickerSlots,
                        textField: {
                          ...completionPickerSlots.textField,
                          required: isCompleted,
                          error: fieldHasError('completion_date'),
                          helperText: fieldHelper('completion_date'),
                          onClick: openPicker,
                          onKeyDown: (e) => {
                            if (completionPickerDisabled) return;
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openPicker();
                            }
                          },
                        },
                        openPickerButton: {
                          onClick: (e) => {
                            e.stopPropagation();
                            openPicker();
                          },
                        },
                      };
                    })()}
                  />

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      background: alpha(NEON, 0.04),
                      border: `1px solid ${alpha(NEON, 0.14)}`,
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                    }}
                  >
                    <InfoIcon sx={{ color: alpha(NEON, 0.55), flexShrink: 0, mt: 0.15, fontSize: 18 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.82), lineHeight: 1.45 }}>
                        Set status to <Box component="span" sx={{ color: NEON, fontWeight: 600 }}>Completed</Box> to unlock completion date.
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha(TEXT_BODY, 0.72), lineHeight: 1.45 }}>
                        Completion date defaults to today when status changes to Completed; adjust it if the work was finished on another day.
                      </Typography>
                      {isCompleted && formData.completion_date && (
                        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.68rem', color: alpha(NEON, 0.65), letterSpacing: 0.2, mt: 0.25 }}>
                          Selected: {dayjs(formData.completion_date).format('dddd, D MMMM YYYY')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                </Box>
              </SectionCard>
            )}

            <SubmitActionsCard
              statusText={isEditMode ? 'Save changes to update this maintenance request.' : 'Complete the form to create a new maintenance request.'}
              missingRequiredFields={missingRequiredFields}
              readyStatusText="All required fields are complete. Ready to create maintenance request."
              cancelLabel="Cancel"
              onCancel={() => navigate(-1)}
              cancelDisabled={loading}
              submitLabel={isEditMode ? 'Update Request' : 'Create Request'}
              submitDisabled={loading || !canSubmit}
              loading={loading}
              submitStartIcon={<SaveIcon />}
              {...submitDraftProps}
            />

          </Box>
        </form>

        <ConfirmUpdateDialog
          open={confirmUpdateDialogOpen}
          onClose={() => setConfirmUpdateDialogOpen(false)}
          onConfirm={confirmUpdate}
          title="Confirm Update"
          variant="edit"
          itemType="maintenance"
          itemName={formData.title}
          description="This action will update the maintenance request details and cannot be undone."
          confirmLabel="Update Request"
          loading={loading}
          confirmDisabled={!canSubmit}
        />

      </Container>
    </LocalizationProvider>
  );
};

export default MaintenanceForm;
